// CR460: Hook to fetch and manage step attempts for a batch run
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface StepAttempt {
  attempt_id: string;
  step_key: string;
  attempt_no: number;
  status: string;
  config_json: Record<string, unknown> | null;
  config_hash: string | null;
  input_ocr_attempt_id: string | null;
  records_in: number | null;
  records_out: number | null;
  duration_ms: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  error_message: string | null;
}

export type SyncStatus = "IN_SYNC" | "OUT_OF_SYNC" | "NO_AI" | "UNKNOWN";

export interface StepAttemptsState {
  ocrAttempts: StepAttempt[];
  aiAttempts: StepAttempt[];
  selectedOcrAttemptId: string | null;
  selectedAiAttemptId: string | null;
  syncStatus: SyncStatus;
  loading: boolean;
}

/** Compute sync status between a selected OCR attempt and a selected AI attempt */
function computeSyncStatus(ocrAttemptId: string | null, aiAttempt: StepAttempt | null): SyncStatus {
  if (!aiAttempt) return "NO_AI";
  if (!ocrAttemptId) return "UNKNOWN";
  if (aiAttempt.input_ocr_attempt_id === ocrAttemptId) return "IN_SYNC";
  if (aiAttempt.input_ocr_attempt_id) return "OUT_OF_SYNC";
  return "UNKNOWN"; // Legacy: no linkage recorded
}

export function useStepAttempts(batchRunId: string) {
  const [state, setState] = useState<StepAttemptsState>({
    ocrAttempts: [],
    aiAttempts: [],
    selectedOcrAttemptId: null,
    selectedAiAttemptId: null,
    syncStatus: "UNKNOWN",
    loading: true,
  });

  const fetchAttempts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("id, step_key, attempt_no, status, config_json, config_hash, input_ocr_attempt_id, records_in, records_out, duration_ms, started_at, finished_at, created_at, error_message")
        .eq("batch_run_id", batchRunId)
        .in("step_key", ["S1_OCR", "S1_AI", "S1_SNIPPET"])
        .order("attempt_no", { ascending: false });

      if (error) {
        console.error("[CR460] Failed to fetch attempts:", error);
        return;
      }

      const rows = (data || []) as Array<{
        id: string;
        step_key: string;
        attempt_no: number;
        status: string;
        config_json: Record<string, unknown> | null;
        config_hash: string | null;
        input_ocr_attempt_id: string | null;
        records_in: number | null;
        records_out: number | null;
        duration_ms: number | null;
        started_at: string | null;
        finished_at: string | null;
        created_at: string;
        error_message: string | null;
      }>;

      // CR469/CR471: SUPERSEDED attempts are hidden; FAILED attempts are visible for Retry state
      const HIDDEN_STATUSES = ["SUPERSEDED"];
      const normalizeStatus = (status: string | null | undefined) => (status ?? "").toUpperCase().trim();

      const ocrAttempts: StepAttempt[] = rows
        .filter((r) => r.step_key === "S1_OCR")
        .map((r) => ({ ...r, attempt_id: r.id, status: normalizeStatus(r.status) }));

      const aiAttempts: StepAttempt[] = rows
        .filter((r) => r.step_key === "S1_AI")
        .map((r) => ({ ...r, attempt_id: r.id, status: normalizeStatus(r.status) }));

      // CR471: For default selection, prefer COMPLETE, then latest non-hidden
      const activeOcr = ocrAttempts.filter((a) => !HIDDEN_STATUSES.includes(normalizeStatus(a.status)));
      const activeAi = aiAttempts.filter((a) => !HIDDEN_STATUSES.includes(normalizeStatus(a.status)));

      const pickPreferredAttempt = (attempts: StepAttempt[], preferRunning: boolean) => {
        // For OCR, always bind to the current run attempt when present to avoid stale
        // truth-table views from older completed attempts.
        const priority = preferRunning
          ? ["RUNNING", "QUEUED", "COMPLETE", "PARTIAL", "COMPLETE_EMPTY", "SKIPPED", "FAILED"]
          : ["COMPLETE", "PARTIAL", "COMPLETE_EMPTY", "SKIPPED", "RUNNING", "QUEUED", "FAILED"];
        for (const status of priority) {
          const found = attempts.find((a) => normalizeStatus(a.status) === status);
          if (found) return found;
        }
        return attempts[0];
      };

      const latestOcr = pickPreferredAttempt(activeOcr.length ? activeOcr : ocrAttempts, true);
      const latestAi = pickPreferredAttempt(activeAi.length ? activeAi : aiAttempts, false);

      // Determine sync status
      const syncStatus = computeSyncStatus(latestOcr?.attempt_id ?? null, latestAi ?? null);

      setState({
        ocrAttempts,
        aiAttempts,
        selectedOcrAttemptId: latestOcr?.attempt_id ?? null,
        selectedAiAttemptId: latestAi?.attempt_id ?? null,
        syncStatus,
        loading: false,
      });
    } catch (err) {
      console.error("[CR460] Error fetching attempts:", err);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [batchRunId]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  // Keep attempt selection synced while run state changes in other panels.
  useEffect(() => {
    const timer = setInterval(() => {
      fetchAttempts();
    }, 3000);
    return () => clearInterval(timer);
  }, [fetchAttempts]);

  const selectOcrAttempt = useCallback((attemptId: string) => {
    setState((prev) => {
      // CR461: Use the currently selected AI attempt (not latest) for sync
      const selectedAi = prev.aiAttempts.find((a) => a.attempt_id === prev.selectedAiAttemptId) ?? null;
      const syncStatus = computeSyncStatus(attemptId, selectedAi);
      return { ...prev, selectedOcrAttemptId: attemptId, syncStatus };
    });
  }, []);

  const selectAiAttempt = useCallback((attemptId: string) => {
    setState((prev) => {
      // CR461: Recompute sync against selectedOcrAttemptId
      const selectedAi = prev.aiAttempts.find((a) => a.attempt_id === attemptId) ?? null;
      const syncStatus = computeSyncStatus(prev.selectedOcrAttemptId, selectedAi);
      return { ...prev, selectedAiAttemptId: attemptId, syncStatus };
    });
  }, []);

  return {
    ...state,
    selectOcrAttempt,
    selectAiAttempt,
    refetchAttempts: fetchAttempts,
  };
}
